import React from "react";
import ViewDocument from "../../ui/ViewDocument";

const CareIndia = () => {
  return (
    <div
      id="care-economy-india"
      className="space-y-4 text-softBlack leading-loose text-base font-normal scroll-mt-24"
    >
      <h2 className="text-xl font-bold !mt-10 mb-4">
        The care economy in India
      </h2>

      <h3 className="text-base font-bold mt-4 mb-3">
        Current status of the care economy
      </h3>
      <p>
        There have been growing conversations in India about strengthening the
        care economy. In February 2024, Niti Ayog published{" "}
        <a
          href="https://www.niti.gov.in/sites/default/files/2024-02/Senior%20Care%20Reforms%20in%20India%20FINAL%20FOR%20WEBSITE_compressed.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          guidelines
        </a>{" "}
        on reforms in senior care, and a consortium of development partners –
        supported by the Ministry of Women and Child Development (WCD) and the
        Bill and Melinda Gates Foundation (BMGF) — published a{" "}
        <a
          href="https://static.pib.gov.in/WriteReadData/specificdocs/documents/2024/mar/doc202435319501.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          policy brief
        </a>{" "}
        on formulating strategies for India’s care economy the following month.
      </p>
      <p>
        The brief highlights data from the United Nations Population Fund on
        groups requiring care, noting that about as of 2022, 25 percent (360
        million) of India’s population was aged between 0-14 years and 10.5
        percent (147 million) was over 60 years of age. By 2050, the number of
        elderly persons is expected to almost double to 347 million persons,
        while the child population is projected to decline to 300 million. This
        signifies an increased care load, which will require fortified
        healthcare, childcare, and a robust social support system.
      </p>

      <h3 className="text-base font-semibold italic">
        Women’s labor force participation
      </h3>
      <p>
        According to the WCD-BMGF policy brief, developing India’s care economy
        is critical to the country’s long-term development and to bridge the
        gender gap in India’s workforce. The labor force participation rate for
        women in India was 32.8 percent, according to{" "}
        <a
          href="https://genderdata.worldbank.org/en/economies/india"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          ILO estimates
        </a>{" "}
        for 2024. Data from the{" "}
        <a
          href="https://pib.gov.in/PressReleasePage.aspx?PRID=2057970"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          Periodic Labour Force Survey
        </a>{" "}
        (July 2023-June 2024), show women’s labor force participation rate was
        41.7 percent compared to 78.8 percent for men.
      </p>
      <p>
        The care load is not equally divided in any country. However, the Time
        Use Survey of the National Statistical Organization, 2019, cited in the
        brief, shows that almost 93 percent of working women performed unpaid
        domestic services for household members, against only 31 percent of
        working men. The survey also found that women spent 5.6 hours on unpaid
        work daily, versus 30 minutes for men.
      </p>
      <p>
        The WCD-BMGF brief also presents estimates that the value of women’s
        unpaid domestic and care work in India ranged between 15-17 percent of
        the GDP in 2019-2020, equating to about INR 34.5 lakh crores (USD 431.25
        billion) at the upper end of the estimate. It also cites 2015 data from
        McKinsey Global Institute, stating that India has the potential for a
        GDP rise of 60 percent, if the participation of women in the labor force
        is increased.
      </p>

      <h3 className="text-base font-bold !mt-5">Expert recommendations</h3>
      <p>
        The policy brief provides recommendations from Indian government
        officials at national and state levels, representatives of organizations
        led by communities and women, private sector leaders, and industry
        heads. These include:
      </p>

      <ul className="space-y-4 list-decimal ml-6">
        <li className="italic">Strengthening childcare services</li>
        <ul className="list-disc list-outside">
          <li>
            Provision of safe, affordable, and accessible childcare services to
            increase women’s participation in the labor force.
          </li>
          <li>
            Investments in the establishment of childcare institutions,
            particularly creches, located near workplaces and work sites
          </li>
          <li>
            Ensuring the availability of trained personnel and extending the
            operational hours of creches
          </li>
          <li>
            Collaborations between the government, community-based
            organizations, and the private sector to establish childcare
            infrastructure
          </li>
        </ul>

        <li className="italic">Expanding elderly care</li>
        <ul className="list-disc list-outside">
          <li>Developing home care and institutional care services</li>
          <li>
            Establishing community driven multigenerational activity centers
          </li>
          <li>
            Introducing legal mechanisms allowing old age homes to issue death
            certificates and provide emergency care services
          </li>
          <li>
            Increasing male representation among care workers for elderly
            persons, due to the high demand from elderly men.
          </li>
          <li>
            Involving college students and young people in elder care was also
            suggested.
          </li>
        </ul>

        <li className="italic">Skill training for care workers</li>
        <ul className="list-disc list-outside">
          <li>Creating specialist roles for care workers</li>
          <li>
            Introducing a comprehensive and competency-based skilling framework,
            and professional training modules for care work, with the National
            Skill Development Council (NSDC) as the lead agency
          </li>
        </ul>
      </ul>

      <h3 className="text-base font-bold !mt-5">Overall recommendations</h3>
      <p>
        The brief’s overall recommendations are categorized under five pillars.
        Across all pillars, implementation is envisioned through multi-sectoral
        collaboration, with action initiated and coordinated by the government.
      </p>

      <ul className="list-decimal list-outside space-y-2 ml-6">
        <li className="italic !my-3">
          Leave policies
          <br />
          These recommendations largely focus on providing parental leave
          (including maternal leave, paternal leave, and gender-neutral leave)
          for childcare, and include:
        </li>
        <ul className="list-disc list-outside ml-2">
          <li>
            Introducing financial support by the government for registered
            micro, small and medium enterprises (MSMEs) and start-ups to support
            costs of maternity leave
          </li>
          <li>
            Establishing a regulatory framework to support the introduction of
            market-based financial instruments, such as parental leave
            insurance, that provide monthly payouts
          </li>
          <li>
            Issuing advisories to companies for flexible leave and work policies
          </li>
        </ul>

        <li className="italic !my-3">
          Subsidies for care services through existing or new
          centrally-sponsored schemes
        </li>
        <ul className="list-disc list-outside ml-2">
          <li>
            Providing incentives for NGOs or Self-help Groups (SHGs) providing
            childcare in rural and urban areas
          </li>
          <li>
            Providing incentives for NGOs and SHGs providing elderly care in
            rural and urban areas
          </li>
          <li>
            Offering corporate tax credits for women-led MSMEs providing
            childcare, elder care, and long-term care
          </li>
        </ul>

        <li className="italic !my-3">Investment in care infrastructure</li>
        <ul className="list-disc list-outside ml-2">
          <li>
            Increasing public investment to establish more childcare centers
            including anganwadis
          </li>
          <li>
            Establishing multipurpose care facilities for childcare, elderly
            care, and persons with disabilities through a new
            centrally-sponsored scheme
          </li>
          <li>
            Developing public-private partnership frameworks to mobilize
            investment in care infrastructure
          </li>
        </ul>

        <li className="italic !my-3">Skill training for care workers</li>
        <ul className="list-disc list-outside ml-2">
          <li>
            Enabling the National Skill Development Council to develop job
            roles, certifications, and modules based on seniority and
            specializations for child care, elderly care, or long-term care
          </li>
          <li>
            Incentivizing private institutes to introduce training modules on
            care services
          </li>
          <li>
            Creating government-to-government and business-to-business
            partnerships for capacity building and certification of care workers
          </li>
        </ul>

        <li className="italic !my-3">
          Institutionalizing quality assurance mechanisms
        </li>
        <ul className="list-disc list-outside ml-2">
          <li>
            Establishing standards for services and infrastructure provided by
            private, public, and community-based care service providers
          </li>
          <li>
            Establishing mechanisms to collection and analyze
            gender-disaggregated data on paid and unpaid care work
          </li>
          <li>
            Establishing a Care Sector Skills Council to set standards for human
            resources, including qualifications, wages, and performance metrics
          </li>
        </ul>
      </ul>

      <ViewDocument
        description="This document contains a list of schemes, policies and practices in India, which directly and indirectly benefit caregivers and their dependents"
        documentUrl="https://drive.google.com/file/d/1MNec-9e1UyMVxpzvkRomuboGF7D541Fh/view?usp=sharing"
        className="!my-8"
      />
    </div>
  );
};

export default CareIndia;
